import * as CookieConsent from "vanilla-cookieconsent";
import translations from "./cookie-strings.json";

let initializing = false;
let runPromise: Promise<void> = Promise.resolve();

export function useCookieConsent(autoShow = false) {
    if (import.meta.server) {
        return { showPreferences: () => {} };
    }

    const { gtag } = useGtag();
    const { locale } = useI18n();
    const { $api } = useNuxtApp();
    const rollbar = useRollbar();

    if (!initializing) {
        initializing = true;

        const trackEvent = (eventName: string) => gtag("event", "cookies_" + eventName);

        async function setPreferences(categories: string[]) {
            if (localStorage) {
                try {
                    const response = await $api<{ Guid: string }>("cookie-consent", {
                        method: "POST",
                        body: {
                            PerformanceAndAnalytics: categories.includes("analytics"),
                            TargetingAndAdvertising: categories.includes("targeting"),
                        },
                    });

                    const newGuid = response.Guid;
                    if (newGuid) {
                        localStorage.setItem(
                            "trusted-parts-cookie-consent-accepted-on",
                            new Date().getTime().toString()
                        );
                        localStorage.setItem("trusted-parts-cookie-consent-accepted-guid", newGuid);
                    }
                } catch (e) {
                    // Best-effort: silently ignore network errors when saving preferences
                    rollbar.error("Cookie Consent: failed to set preferences", e as Error);
                }
            }
        }

        import("cookieconsent.css").catch(console.error);

        runPromise = CookieConsent.run({
            autoShow,
            hideFromBots: false,
            language: { default: locale.value, translations },
            autoClearCookies: true,
            manageScriptTags: false,
            mode: "opt-out",
            categories: {
                necessary: { readOnly: true, enabled: true },
                analytics: { enabled: true },
                targeting: { enabled: true },
            },

            onFirstConsent: ({ cookie }) => {
                // Did the user accept all or customize accepted cookies?
                if (cookie.categories.includes("analytics") && cookie.categories.includes("targeting")) {
                    trackEvent("overlay_accept");
                } else {
                    trackEvent("modal_save");
                    if (!cookie.categories.includes("analytics")) trackEvent("modal_disable_analytics");
                    if (!cookie.categories.includes("targeting")) trackEvent("modal_disable_targeting");
                }

                setPreferences(cookie.categories || []);
            },

            onChange: ({ cookie, changedCategories }) => {
                // If this was fired as part of the initial interaction, it's not actually a change
                if (cookie.consentTimestamp === cookie.lastConsentTimestamp) {
                    return;
                }

                trackEvent("modal_save");

                if (changedCategories) {
                    ["targeting", "analytics"].forEach((category) => {
                        if (changedCategories.includes(category)) {
                            const isEnabled = cookie.categories.includes(category);
                            trackEvent(["modal", isEnabled ? "enable" : "disable", category].join("_"));
                        }
                    });
                }

                setPreferences(cookie.categories || []);
            },
        }).catch((error) => {
            initializing = false; // Allow retry on next call
            rollbar.error("Cookie Consent: initialization failed", error);
        });
    }

    return {
        showPreferences() {
            runPromise.then(() => CookieConsent.showPreferences());
        },
    };
}
